import type { CSSProperties } from 'react'
import type { ResizableProps } from 'react-resizable'

import { COLOR_YELLOW } from '@/constants/constants'
import { dimHexToRgba, hexToRgba } from '@/utils/colors'

// Figma 1104:57636: the outline a note gets on hover, and the ring around a
// selected one, is the note's own colour 20 brightness points darker. Figma has
// both at 25%; the hover outline was too faint at that and goes at 75%.
const OUTLINE_BRIGHTNESS_DROP = 20
const OUTLINE_ALPHA = 0.75
const RING_ALPHA = 0.25

// A selected note is not yet a text field: the next click makes it one, so the
// pointer stays until the editor actually has focus.
const getNoteCursor = (
  isDragging: boolean,
  isEditing: boolean,
  isDisabled: boolean
): CSSProperties['cursor'] => {
  if (isEditing) {
    return 'text'
  }

  if (isDisabled) {
    return 'default'
  }

  if (isDragging) {
    return 'move'
  }

  return 'pointer'
}

const getDimmedColor = (color: string, alpha: number): string =>
  dimHexToRgba(color, OUTLINE_BRIGHTNESS_DROP, alpha) ??
  (dimHexToRgba(COLOR_YELLOW, OUTLINE_BRIGHTNESS_DROP, alpha) as string)

const useNoteStyle = (
  color: string,
  isDragging: boolean,
  isEditing: boolean,
  isDisabled: boolean,
  size: ResizableProps
) => {
  const noteColor = color || COLOR_YELLOW

  return {
    backgroundColor: color,
    cursor: getNoteCursor(isDragging, isEditing, isDisabled),
    width: `${size.width}px`,
    minHeight: `${size.height}px`,
    '--sticky-note-selected-border-color': hexToRgba(noteColor, 0.5),
    '--sticky-note-outline-color': getDimmedColor(noteColor, OUTLINE_ALPHA),
    '--sticky-note-ring-color': getDimmedColor(noteColor, RING_ALPHA),
  } as Partial<CSSProperties>
}

export default useNoteStyle
